import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router';
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import ImagePicker from '../components/ImagePicker';
import { RichTextEditor } from '../components/RichTextEditor';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

export default function BroadcastEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [broadcast, setBroadcast] = React.useState<any>(null);
  const [cities, setCities] = React.useState<any[]>([]);
  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [scheduledDate, setScheduledDate] = React.useState<Dayjs | null>(
    dayjs(),
  );
  const [scheduledTime, setScheduledTime] = React.useState<Dayjs | null>(
    dayjs(),
  );
  const [mediaId, setMediaId] = React.useState<number | null>(null);

  React.useEffect(() => {
    fetch(`${API_URL}/broadcast/${id}`)
      .then((r) => r.json())
      .then(setBroadcast);

    fetch(`${API_URL}/cities`)
      .then((r) => r.json())
      .then((r) => setCities(r.cities));
  }, [id]);

  React.useEffect(() => {
    if (!broadcast) return;

    setScheduledDate(
      broadcast.scheduledDateAt
        ? dayjs(broadcast.scheduledDateAt, 'DD.MM.YYYY')
        : null,
    );
    setScheduledTime(
      broadcast.scheduledTimeAt
        ? dayjs(broadcast.scheduledTimeAt, 'HH:mm')
        : null,
    );

    if (broadcast.broadcastMedia) {
      if (broadcast.broadcastMedia.imageUrl) {
        setImageUrl(`${IMAGE_URL}/${broadcast.broadcastMedia.imageUrl}`);
      }

      if (broadcast.broadcastMedia.mediaId) {
        setMediaId(broadcast.broadcastMedia.mediaId);
      }
    }
  }, [broadcast]);

  if (!broadcast) return null;

  const save = () => {
    const formData = new FormData();

    formData.append('message', broadcast.message);
    formData.append('cityId', broadcast.cityId ?? '');
    formData.append('scheduledDateAt', broadcast.scheduledDateAt);
    formData.append('scheduledTimeAt', broadcast.scheduledTimeAt);
    formData.append('channelType', broadcast.channelType ?? 'all');
    formData.append('auditory', broadcast.auditory ?? 'all');
    if (image) {
      formData.append('image', image);
    } else if (mediaId) {
      formData.append('mediaId', String(mediaId));
    }

    fetch(`${API_URL}/broadcast/${id}`, {
      method: 'PUT',
      body: formData,
    }).then(() => navigate('/broadcasts'));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: 700 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Редагування розсилки
        </Typography>

        <RichTextEditor
          value={broadcast.message}
          onChange={(html) => setBroadcast({ ...broadcast, message: html })}
          label="Повідомлення"
          minRows={4}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Місто</InputLabel>
          <Select
            value={broadcast.cityId ?? ''}
            onChange={(e) =>
              setBroadcast({
                ...broadcast,
                cityId: e.target.value || null,
              })
            }
          >
            <MenuItem value="">Усі міста</MenuItem>
            {cities.map((c) => (
              <MenuItem key={c.citiesId} value={c.citiesId}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Канал</InputLabel>
          <Select
            value={broadcast.channelType ?? 'all'}
            onChange={(e) =>
              setBroadcast({ ...broadcast, channelType: e.target.value })
            }
          >
            <MenuItem value="viber">Viber</MenuItem>
            <MenuItem value="telegram">Telegram</MenuItem>
            <MenuItem value="all">Всі</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Аудиторія</InputLabel>
          <Select
            value={broadcast.auditory ?? 'all'}
            onChange={(e) =>
              setBroadcast({ ...broadcast, auditory: e.target.value })
            }
          >
            <MenuItem value="all">Всі</MenuItem>
            <MenuItem value="new">Нові користувачі</MenuItem>
            <MenuItem value="bot">Аудиторія бота</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DatePicker
            label="Дата відправки"
            format="DD.MM.YYYY"
            value={scheduledDate}
            onChange={(v) => {
              setScheduledDate(v);

              setBroadcast({
                ...broadcast,
                scheduledDateAt: v
                  ? v.format('DD.MM.YYYY')
                  : dayjs().format('DD.MM.YYYY'),
              });
            }}
          />

          <TimePicker
            label="Час відправки"
            format="HH:mm"
            value={scheduledTime}
            onChange={(v) => {
              setScheduledTime(v);

              setBroadcast({
                ...broadcast,
                scheduledTimeAt: v
                  ? v.format('HH:mm')
                  : dayjs().format('HH:mm'),
              });
            }}
          />
        </Box>

        <ImagePicker
          title="Зображення"
          value={imageUrl || ''}
          onChange={(value) => {
            if (value.type === 'file') {
              setImage(value.file);
              setMediaId(null);
              setImageUrl(URL.createObjectURL(value.file));
            }

            if (value.type === 'media') {
              setImage(null);
              setMediaId(value.mediaId);
              setImageUrl(value.url);
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={save}>
            Зберегти
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Скасувати
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
